import { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return (JSON.parse(localStorage.getItem("regal_cart")) || []).map((i) => ({ ...i, key: i.key || i.slug, currency: i.currency || "GBP" })); } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("regal_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, qty = 1, meta = null) => {
    const key = product.slug + "|" + (meta ? JSON.stringify(meta.options || meta.master_company || "") : "");
    const price = meta?.total ?? product.effective_price ?? product.price_ex_vat;
    const name = meta?.label || product.name;
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => i.key === key ? { ...i, qty: Math.min(i.qty + qty, 20) } : i);
      return [...prev, { key, slug: product.slug, name, price, currency: meta?.currency || product.currency || "GBP", image: product.image, badge: product.badge, qty, meta, physical: product.physical ?? (product.category ? product.category !== "subscriptions" : true) }];
    });
    toast.success("Added to cart", { description: name });
  }, []);

  const removeItem = useCallback((key) => setItems((p) => p.filter((i) => i.key !== key)), []);
  const setQty = useCallback((key, qty) => {
    if (qty < 1) return;
    setItems((p) => p.map((i) => i.key === key ? { ...i, qty: Math.min(qty, 20) } : i));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((a, i) => a + i.qty, 0);
    const subtotal = items.reduce((a, i) => a + i.price * i.qty, 0);
    return { items, count, subtotal, vat: 0, total: subtotal, addItem, removeItem, setQty, clear, open, setOpen };
  }, [items, addItem, removeItem, setQty, clear, open]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
